import { FC, useState, useEffect } from 'react'
import { Conversation, CreateConversationOptions } from '@twilio/conversations'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { getParticipantIdsByConversation } from 'features/MerinioChat/helpers'
import { Card, Input, Button } from 'ui'

type Props = {
  users: MData.User[]
  currentUser: MData.CurrentUser['userInfo']
  createConversation: (options: CreateConversationOptions) => Promise<Conversation | undefined>
  conversations: Conversation[] | undefined
  setShouldHideList: (shouldHideList: boolean) => void
  setActiveConversation: (conversation: Conversation | null) => void
}

type Option = {
  value: string
  label: string
}

const CreateConversation: FC<Props> = ({ users, currentUser, setShouldHideList, createConversation, conversations, setActiveConversation }) => {
  const { t } = useTranslation('legacy')

  const [selectedUsers, setSelectedUsers] = useState<Option[]>([])
  const [loading, setLoading] = useState(false)
  const [convoFriendlyName, setConvoFriendlyName] = useState('')

  useEffect(() => {
    if (selectedUsers.length > 0) {
      setShouldHideList(true)
    } else {
      setShouldHideList(false)
    }
  }, [selectedUsers, setShouldHideList])

  if (!currentUser) return null

  const options = users
    .filter(user => user._id !== currentUser._id && !user.archived)
    .sort((a, b) => a.fullName.localeCompare(b.fullName))
    .map(user => {
      if (user._id !== currentUser._id) {
        return {
          value: user._id,
          label: user.fullName
        }
      }
    })
    .filter(Boolean)

  const handleConvoFriendlyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConvoFriendlyName(e.target.value)
  }

  const checkConversationExist = async (conversations: Conversation[]): Promise<boolean | Conversation> => {
    const requestedParticipants = [...selectedUsers.map(user => user.value), currentUser._id]

    for (const convo of conversations) {
      const participants = await getParticipantIdsByConversation(convo)

      if (participants.length === requestedParticipants.length && participants.every(id => id && requestedParticipants.includes(id))) {
        return convo
      }
    }

    return false
  }

  const handleAddPartcipants = async (conversation: Conversation): Promise<void> => {
    const participantsToAdd = selectedUsers.map(user => user.value)
    await Promise.all(participantsToAdd.map(participantId => conversation.add(participantId)))
  }

  const handleCreateConversation = async (): Promise<void> => {
    setLoading(true)
    if (!conversations) return
    const type = selectedUsers.length > 1 ? 'group' : 'direct'

    const convoExists = await checkConversationExist(conversations)

    if (convoExists instanceof Conversation) {
      setActiveConversation(convoExists)
    } else {
      const conversation = await createConversation({ attributes: { type }, friendlyName: convoFriendlyName ?? null })
      if (!conversation) return

      await conversation.join()
      await handleAddPartcipants(conversation)
      setActiveConversation(conversation)
    }

    setSelectedUsers([])
    setLoading(false)
  }

  return (
    <Card>
      <Card.Body>
        <Select
          value={selectedUsers}
          options={options}
          onChange={selected => {
            setSelectedUsers([...selected])
          }}
          isMulti
          placeholder={t('chat.CONVERSATIONS.SELECT_PARTICIPANTS')}
        />
        {selectedUsers.length >= 2 && (
          <div className="input-group mt-3">
            <Input onChange={handleConvoFriendlyNameChange} value={convoFriendlyName} type="text" placeholder={t('chat.CONVO.CUSTOM_NAME')} />
          </div>
        )}
        <Button color="primary" onClick={handleCreateConversation} className="mt-4" disabled={selectedUsers.length === 0 || loading}>
          {t('chat.CONVERSATIONS.CREATE_CONVERSATION')}
        </Button>
      </Card.Body>
    </Card>
  )
}

export { CreateConversation }
